<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Notifications',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Object,
      required: false,
    },
    panelHeight: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const refreshedItems = ref(null);
  const ux = ref({
    loadingPlaceholder: 'none',
  });
  const b_2248226175642056_style = computed(() => ({
    height: props.panelHeight || '600px',
    overflow: 'hidden',
  }));
  const b_3343864469950044_style = computed(() => ({
    overflow: 'auto',
    minHeight: 0,
  }));

  const $data = computed(() => ({
    refreshedItems: refreshedItems.value,
    ux: ux.value,
    b_2248226175642056_style: b_2248226175642056_style.value,
    b_3343864469950044_style: b_3343864469950044_style.value,
  }));

  const $$load_refreshedItems = async () => {
    return null;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'refreshedItems' },
      { name: 'ux' },
      { name: 'b_2248226175642056_style', nodeId: '2248226175642056' },
      { name: 'b_3343864469950044_style', nodeId: '3343864469950044' },
    ],
    loaders: { refreshedItems: $$load_refreshedItems },
    ux: { loadingPlaceholder: 'none' },
    dataRefs: {
      refreshedItems,
      ux,
      b_2248226175642056_style,
      b_3343864469950044_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const list = computed(() => {
    if (loaded.value) {
      return refreshedItems.value || props.items || [];
    }
    return null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.panelHeight,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('panelHeight');
    }
  );

  const act_8697745105058001_setData_t5tp82486_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'refreshedItems',
      value: _.orderBy($event.data.response.data, ['created_at'], ['desc']),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8697745105058001_request_w7b375847_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'list_notification',
      parameters: {
        state: 'new',
      },
    };
  };
  const act_1840158740026147_setData_stswgzlm5_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'refreshedItems',
      value: _.reject(list.value, { id: $event.scope.item.id }),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1840158740026147_request_3z7wf6vr5_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_notification',
      data: {
        id: $event.scope.item.id,
        etag: $event.scope.item.etag,
        state: 'handled',
        result_notes: null,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_ldso2vaf4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_x2plszj5v =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ldso2vaf4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_x2plszj5v],
    event: 'reload',
  });
  const act_8697745105058001_setData_t5tp82486 = new actionTypes.SetDataAction({
    actionArgs: act_8697745105058001_setData_t5tp82486_getActionArgs,
  });
  const inst_evh_8697745105058001_success_h2arx3j7p =
    new actionTypes.EventHandlerImpl({
      action: act_8697745105058001_setData_t5tp82486,
      event: 'success',
      displayName: 'setData',
    });
  const act_8697745105058001_request_w7b375847 = new actionTypes.RequestAction({
    actionArgs: act_8697745105058001_request_w7b375847_getActionArgs,
    displayName: 'reload',
    events: [inst_evh_8697745105058001_success_h2arx3j7p],
  });
  const inst_evh_8697745105058001_click_h8qqyqxwb =
    new actionTypes.EventHandlerImpl({
      action: act_8697745105058001_request_w7b375847,
      event: 'click',
      displayName: 'reload',
    });
  const inst_evh_8697745105058001_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8697745105058001_click_h8qqyqxwb],
    event: 'click',
  });
  const act_1840158740026147_setData_stswgzlm5 = new actionTypes.SetDataAction({
    actionArgs: act_1840158740026147_setData_stswgzlm5_getActionArgs,
  });
  const inst_evh_1840158740026147_success_qsvbm4oqr =
    new actionTypes.EventHandlerImpl({
      action: act_1840158740026147_setData_stswgzlm5,
      event: 'success',
      displayName: 'setData',
    });
  const act_1840158740026147_request_3z7wf6vr5 = new actionTypes.RequestAction({
    actionArgs: act_1840158740026147_request_3z7wf6vr5_getActionArgs,
    displayName: 'markRead',
    events: [inst_evh_1840158740026147_success_qsvbm4oqr],
  });
  const inst_evh_1840158740026147_click_jy0yt1w60 =
    new actionTypes.EventHandlerImpl({
      action: act_1840158740026147_request_3z7wf6vr5,
      event: 'click',
      displayName: 'markRead',
    });
  const inst_evh_1840158740026147_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1840158740026147_click_jy0yt1w60],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_x2plszj5v,
    inst_evh_7315092382398562_reload,
    inst_evh_8697745105058001_success_h2arx3j7p,
    inst_evh_8697745105058001_click_h8qqyqxwb,
    reload: inst_evh_8697745105058001_click_h8qqyqxwb,
    inst_evh_8697745105058001_click,
    inst_evh_1840158740026147_success_qsvbm4oqr,
    inst_evh_1840158740026147_click_jy0yt1w60,
    markRead: inst_evh_1840158740026147_click_jy0yt1w60,
    inst_evh_1840158740026147_click,
  };

  const evh_7315092382398562_reload_x2plszj5v = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_x2plszj5v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_success_h2arx3j7p = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_success_h2arx3j7p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_click_h8qqyqxwb = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_click_h8qqyqxwb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_click = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1840158740026147_success_qsvbm4oqr = async (event, scope) => {
    return await actions.inst_evh_1840158740026147_success_qsvbm4oqr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1840158740026147_click_jy0yt1w60 = async (event, scope) => {
    return await actions.inst_evh_1840158740026147_click_jy0yt1w60.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1840158740026147_click = async (event, scope) => {
    return await actions.inst_evh_1840158740026147_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    loadingPlaceholder="none"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="card" :style="b_2248226175642056_style">
      <div class="card-header d-flex align-items-center">
        <div>
          <span>
            {{ gettext('Notifications') }}
          </span>
          <span class="badge text-bg-secondary ms-2" v-if="list && list.length">
            {{ list.length }}
          </span>
        </div>
        <div class="ms-auto"></div>
        <div>
          <ItpButton
            icon="fa6-solid:arrows-rotate"
            :tooltip="gettext('Reload')"
            :spinning="actions.reload.isRunning"
            size="sm"
            variant="link"
            @click="evh_8697745105058001_click($event, {})"
          >
          </ItpButton>
        </div>
      </div>
      <ul
        class="list-group list-group-flush flex-grow-1"
        v-if="list && list.length"
        :style="b_3343864469950044_style"
      >
        <li :key="item.id" class="list-group-item" v-for="item in list">
          <ItpBox>
            <UcExpiringContractNotificationCard
              :notification="item"
              v-if="item.type == 'expiring_contract'"
            >
            </UcExpiringContractNotificationCard>
            <UcInflationIndexedPriceChangeNotificationCard
              :notification="item"
              v-if="item.type == 'inflation_indexed_price_change'"
            >
            </UcInflationIndexedPriceChangeNotificationCard>
            <UcInvoiceImportNotificationCard
              :notification="item"
              v-if="item.type.startsWith('invoice:')"
            >
            </UcInvoiceImportNotificationCard>
            <button
              :aria-label="gettext('Remove')"
              :title="gettext('Remove')"
              class="close ms-auto mt-2 me-2 ps-4 align-self-start"
              @click="evh_1840158740026147_click($event, { item })"
            >
              <span aria-hidden="true"> &times; </span>
            </button>
          </ItpBox>
        </li>
      </ul>
      <div class="card-body flex-grow-1" v-if="loaded && !list.length">
        <p class="card-text">
          {{ gettext('No new notifications.') }}
        </p>
      </div>
    </div>
  </ItpUsercontrol>
</template>
